'use client';

import { useEffect, useSyncExternalStore } from 'react';

/**
 * Puente entre una pantalla y el asistente de IA del dashboard
 * (DashboardCallCenterWidget), sin pasar props por el layout:
 *
 *  - `abrirAsistente()` abre el MISMO panel desde un botón propio de la pantalla.
 *  - `useOcultarLanzadorAsistente(activo)` esconde el botón flotante redondo
 *    mientras la pantalla esté montada (el panel sigue abriéndose).
 *
 * Va por montaje y no por ruta: el editor de Formatos no es una ruta propia
 * (es ?edit=/?new= empujado con pushState sobre la lista) y vive también en
 * /admin/format-templates.
 */

export const EVENTO_ABRIR_ASISTENTE = 'ezdocu:abrir-asistente';

export function abrirAsistente(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(EVENTO_ABRIR_ASISTENTE));
}

// Contador y no booleano: si dos pantallas lo piden, el lanzador vuelve solo
// cuando se desmonta la última.
let ocultadores = 0;
const oyentes = new Set<() => void>();

function avisar(): void {
  oyentes.forEach((fn) => fn());
}

function suscribir(fn: () => void): () => void {
  oyentes.add(fn);
  return () => {
    oyentes.delete(fn);
  };
}

export function lanzadorAsistenteOculto(): boolean {
  return ocultadores > 0;
}

export function useOcultarLanzadorAsistente(activo = true): void {
  useEffect(() => {
    if (!activo) return;
    ocultadores += 1;
    avisar();
    return () => {
      ocultadores = Math.max(0, ocultadores - 1);
      avisar();
    };
  }, [activo]);
}

export function useLanzadorAsistenteOculto(): boolean {
  return useSyncExternalStore(suscribir, lanzadorAsistenteOculto, () => false);
}
